import React, { useState, useMemo, useCallback } from 'react';
import {
  Typography,
  PrimaryButton,
  DefaultButton,
  makeStyles,
  createStyles,
  Theme,
} from '@bb-ui/react-library';
import { Popover } from '@bb-ui/react-library/dist/components/Popover';
import { TextField } from '@bb-ui/react-library/dist/components/TextField';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      padding: theme.spacing(2),
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1.5),
      width: 240,
    },
    actions: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: theme.spacing(1),
    },
  }),
);

interface RetryPresyncPopoverProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onConfirm: (siteId: string, hours: number) => void;
  siteId: string | null;
  downtimeDate: string | null;
  isRetrying?: boolean;
}

const MIN_HOURS_BEFORE_DOWNTIME = 24;

const computeMaxHours = (downtimeDate: string | null): number | null => {
  if (!downtimeDate) return null;
  const availableMs =
    new Date(downtimeDate).getTime() - Date.now() - MIN_HOURS_BEFORE_DOWNTIME * 3600000;
  if (availableMs <= 0) return 0;
  return Math.floor(availableMs / 3600000);
};

const RetryPresyncPopover: React.FC<RetryPresyncPopoverProps> = ({
  anchorEl,
  onClose,
  onConfirm,
  siteId,
  downtimeDate,
  isRetrying = false,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [hours, setHours] = useState<string>('');

  const maxHours = useMemo(() => computeMaxHours(downtimeDate), [downtimeDate]);
  const hoursValue = hours === '' ? null : Number(hours);

  const validationError = useMemo(() => {
    if (hoursValue === null) return null;
    if (hoursValue < 0) return t('cohorts.retryMinHoursError');
    if (maxHours !== null && hoursValue > maxHours) {
      return t('cohorts.retryMaxHoursError', { maxHours });
    }
    return null;
  }, [hoursValue, maxHours, t]);

  const isConfirmDisabled =
    hoursValue === null || hoursValue < 0 || !!validationError || isRetrying;

  const handleConfirm = useCallback(() => {
    if (!siteId || hoursValue === null) return;

    // Re-validate against current time in case user waited too long
    const freshMaxHours = computeMaxHours(downtimeDate);
    if (freshMaxHours !== null && hoursValue > freshMaxHours) return;

    onConfirm(siteId, hoursValue);
  }, [siteId, hoursValue, downtimeDate, onConfirm]);

  const handleClose = useCallback(() => {
    if (!isRetrying) {
      setHours('');
      onClose();
    }
  }, [isRetrying, onClose]);

  const helperText = useMemo(() => {
    if (validationError) return validationError;
    if (maxHours !== null) {
      return (
        <>
          {t('cohorts.retryPresyncDescriptionNoLimit')} Max <strong>{maxHours}h</strong> (
          {MIN_HOURS_BEFORE_DOWNTIME}h before downtime).
        </>
      );
    }
    return t('cohorts.retryPresyncDescriptionNoLimit');
  }, [validationError, maxHours, t]);

  return (
    <Popover
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    >
      <div className={classes.container}>
        <Typography variant="subtitle2">{t('cohorts.retryPresync')}</Typography>

        <TextField
          id="retry-presync-hours"
          label={t('cohorts.presyncHoursLabel')}
          type="number"
          value={hours}
          onChange={(e) => setHours(e.target.value)}
          inputProps={{ min: 0, max: maxHours ?? undefined }}
          helperText={helperText}
          error={!!validationError}
          size="small"
        />

        <div className={classes.actions}>
          <DefaultButton onClick={handleClose} disabled={isRetrying} size="small">
            {t('global.cancel')}
          </DefaultButton>
          <PrimaryButton onClick={handleConfirm} disabled={isConfirmDisabled} size="small">
            {isRetrying ? t('cohorts.retrying') : t('cohorts.retry')}
          </PrimaryButton>
        </div>
      </div>
    </Popover>
  );
};

export default RetryPresyncPopover;
